import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import instance from "api/apiClient";

import { useAppDispatch } from "store";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import RoundBtn from "components/buttons/RoundBtn";

import MainPageDragItem from "pages/CMS/Widgets/components/MainPageDragItem";
import MainPageSortItem from "pages/CMS/Widgets/components/MainPageSortItem";

import WidgetsListSkeleton from "components/global/Skeleton/WidgetsListSkeleton/WidgetsListSkeleton";

import icon from "assets/icons/burger-icon.svg";

import "pages/CMS/Widgets/Widgets.scss";
import { apiRoutes } from "configs/apiRoutes";

interface ISignUpFormItem {
  value: string;
  label: string;
  img?: string;
  type?: string;
  required?: boolean;
  instanceId?: number | null;
}

type Props = {
  readOnly?: boolean;
}

const PresetsSignUpTab: React.FC<Props> = ({ readOnly }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();

  const presetId = searchParams.get("entityId");

  const [burgerItems, setBurgerItems] = useState<
    { value: string; label: string; img: string }[]
  >([]);

  const [pageItems, setPageItems] = useState<ISignUpFormItem[]>([]);

  const [itemsFetched, setItemsFetched] = useState(false);

  const [selectedItems, setSelectedItems] = useState<
    { value: string; label: string }[]
  >([]);

  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState<
    { value: string; label: string }[]
  >([]);

  const [bonusOptions, setBonusOptions] = useState<
    { value: number; label: string; img: string }[]
  >([]);

  useEffect(() => {
    if (presetId) {
      setItemsFetched(false);

      getPageItems();
      getItems();
      getBonuses();
    }
  }, [presetId]);

  const getPageItems = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.presetsItem}/${presetId}/widgets/sign-up`,
      );

      const items = result.data?.items ?? [];

      setPageItems(
        items.map((el: any) => ({
          value: el.instanceType,
          type: el.instanceType,
          label: t(el.instanceType),
          img: icon,
          required: el.required ?? false,
          instanceId:
            el.instanceType === "bonus"
              ? (el.meta?.instanceId ?? null)
              : undefined,
        })),
      );
    } catch (error) {
      console.error(error);
    }

    setItemsFetched(true);
  };

  const getItems = async () => {
    try {
     const result = await instance.get(
       `${apiRoutes.signUpItems}?limit=500&offset=0`,
     );

      setBurgerItems(
        result.data.items.map((el: string) => ({
          value: el,
          label: t(el),
          img: icon,
          type: "burger",
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getBonuses = async () => {
    try {
      const res = await instance.get(apiRoutes.bonusWelcome);

      const items = res.data?.items ?? [];

      setBonusOptions(
        items
          .filter((el: any) => el.status === "activated")
          .map((el: any) => ({
            value: el.id,
            label: el.title,
            img: el.image,
          })),
      );
    } catch (e) {
      console.error("BONUS ERROR:", e);
    }
  };

  const handleUpdateItems = async (items: any[]) => {
    try {
      await instance.put(`${apiRoutes.presetsItem}/${presetId}/widgets/sign-up`, {
        items: items.map((el, idx) => {
          const base = {
            instanceType: el.value,
            priorityOrder: idx + 1,
            required: !!el.required,
          };

          if (el.value === "bonus") {
            return {
              ...base,
              instanceId: el.instanceId,
            };
          }

          return base;
        }),
        isActive: 1,
      });

      setPageItems([]);

      setItemsFetched(false);

      getPageItems();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const moveItem = async (dragIndex: number, hoverIndex: number) => {
    const draggedItem = pageItems[dragIndex];

    if (draggedItem.value === "bonus") {
      return;
    }

    if (hoverIndex === 0 && pageItems[0]?.value === "bonus") {
      return;
    }

    const items = Array.from(pageItems);

    const [reorderedItem] = items.splice(dragIndex, 1);

    items.splice(hoverIndex, 0, reorderedItem);

    setPageItems(items);

    handleUpdateItems(items);
  };

  const normalizeBonusPosition = (items: ISignUpFormItem[]) => {
    const bonus = items.find((el) => el.value === "bonus");

    const others = items.filter((el) => el.value !== "bonus");

    return bonus ? [bonus, ...others] : others;
  };

  return (
    <div className="preset-form-wrapper">
      <div className="preset-form-content preset-signup-tab-wrapper">
        <div className="main-page-wrapper burger-page-wrapper">
          <div className="main-page-content">
            <div className="main-page-widget">
              <div className="main-page-widget--box">
                <div className="main-page-title">{t("selectedItems")}</div>
                {itemsFetched ? (
                  <div className="main-page-widget--list">
                    {pageItems.map((el, index) => (
                      <MainPageSortItem
                        isBurger
                        handleClick={() => {
                          if (selectedItemsToDelete.includes(el)) {
                            setSelectedItemsToDelete(
                              selectedItemsToDelete.filter(
                                (item) => item !== el,
                              ),
                            );
                          } else {
                            setSelectedItemsToDelete([
                              ...selectedItemsToDelete,
                              el,
                            ]);
                          }
                        }}
                        isSelected={selectedItemsToDelete.includes(el)}
                        key={`${el.value}`}
                        item={el}
                        index={index}
                        moveItem={moveItem}
                        onToggleRequired={() => {
                          const updated = [...pageItems];

                          updated[index].required = !updated[index].required;

                          setPageItems(updated);

                          handleUpdateItems(updated);
                        }}
                        bonusOptions={bonusOptions}
                        onSelectBonus={(val, index) => {
                          const updated = [...pageItems];

                          updated[index].instanceId = Number(val);

                          setPageItems(updated);

                          handleUpdateItems(updated);
                        }}
                        isSignUpForm
                        isDraggable={!readOnly}
                        readOnly={readOnly}
                      />
                    ))}
                  </div>
                ) : (
                  <WidgetsListSkeleton />
                )}

                <div className="main-page-btns">
                  {!readOnly && (
                    <>
                      <RoundBtn
                        handleClick={() => {
                          setItemsFetched(false);

                          let newArray = [...pageItems, ...selectedItems];

                          newArray = normalizeBonusPosition(newArray);

                          setSelectedItems([]);

                          handleUpdateItems(newArray);

                          setPageItems(newArray);
                        }}
                        txt="+"
                        disabled={selectedItems.length === 0}
                      />

                      <RoundBtn
                        handleClick={() => {
                          setItemsFetched(false);

                          const newArray = pageItems.filter(
                            (el) => !selectedItemsToDelete.includes(el),
                          );

                          handleUpdateItems(newArray);

                          setSelectedItemsToDelete([]);

                          setPageItems(newArray);
                        }}
                        txt="-"
                        disabled={selectedItemsToDelete.length === 0}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="main-page-widget">
              <div className="main-page-widget--box">
                <div className="main-page-title">{t("menuItems")}</div>

                <div className="main-page-list-content">
                  {itemsFetched ? (
                    <>
                      {burgerItems
                        .filter(
                          (item) =>
                            !pageItems.map((p) => p.value).includes(item.value),
                        )
                        .map((el: any) => (
                          <MainPageDragItem
                            isBurger
                            handleClick={() => {
                              if (selectedItems.includes(el)) {
                                setSelectedItems(
                                  selectedItems.filter((item) => item !== el),
                                );
                              } else {
                                setSelectedItems([...selectedItems, el]);
                              }
                            }}
                            isSelected={selectedItems.includes(el)}
                            key={el.value}
                            item={el}
                            type="burger"
                          />
                        ))}
                    </>
                  ) : (
                    <WidgetsListSkeleton />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetsSignUpTab;
